import React from 'react';
import { DefaultButton, FontWeights, Icon, Stack, Text, Toggle, getTheme } from '@fluentui/react';
import { LogbookEntry } from '../../Model/LogbookEntry';

interface StatisticsBarProps {
    displayedEntries: LogbookEntry[];
    checkedEntries: LogbookEntry[];
    showAllRides: boolean;
    loading: boolean;
    onToggleView: (checked: boolean) => void;
    onRefresh: () => void;
    theme: any;
    styles: any;
}

export const StatisticsBar: React.FC<StatisticsBarProps> = ({
    displayedEntries,
    checkedEntries,
    showAllRides,
    loading,
    onToggleView,
    onRefresh
}) => {
    const fluent = getTheme();
    const totalRevenue = displayedEntries.reduce((sum, item) => sum + item.finalPrice, 0);
    const totalKilometers = displayedEntries.reduce((sum, item) => sum + item.kilometersBillable, 0);

    const metrics = [
        {
            icon: 'Car',
            value: displayedEntries.length.toString(),
            label: showAllRides ? 'Celkem jízd' : 'Nezpracované jízdy',
        },
        {
            icon: 'Money',
            value: `${totalRevenue.toLocaleString('cs-CZ')} Kč`,
            label: showAllRides ? 'Celková tržba' : 'Nezpracovaná tržba',
        },
        {
            icon: 'MapPin',
            value: `${totalKilometers.toLocaleString('cs-CZ')} km`,
            label: showAllRides ? 'Celkem km' : 'Nezpracované km',
        },
        ...(showAllRides ? [{
            icon: 'CheckMark',
            value: checkedEntries.length.toString(),
            label: 'Zpracované jízdy',
        }] : []),
    ];

    const cardStyle: React.CSSProperties = {
        background: fluent.palette.white,
        borderRadius: 10,
        padding: '20px 24px',
        boxShadow: fluent.effects.elevation4,
        marginBottom: 12,
    };

    const metricTileStyle: React.CSSProperties = {
        background: fluent.palette.neutralLighterAlt,
        border: `1px solid ${fluent.palette.neutralQuaternaryAlt}`,
        borderRadius: 8,
        padding: '12px 18px',
        flex: '1 1 140px',
        minWidth: 120,
    };

    return (
        <div style={cardStyle}>
            {/* Header row: title + controls */}
            <Stack
                horizontal
                horizontalAlign="space-between"
                verticalAlign="center"
                wrap
                tokens={{ childrenGap: 12 }}
                style={{ marginBottom: 16 }}
            >
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <Icon iconName="BarChartVertical" style={{ fontSize: 16, color: fluent.palette.themePrimary }} />
                    <Text variant="mediumPlus" style={{ fontWeight: FontWeights.semibold }}>
                        {showAllRides ? 'Všechny jízdy' : 'Nezpracované jízdy'}
                    </Text>
                </Stack>

                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
                    <Toggle
                        inlineLabel
                        label="Zobrazit vše"
                        checked={showAllRides}
                        onChange={(_, checked) => onToggleView(checked ?? false)}
                        onText="Ano"
                        offText="Ne"
                        styles={{ root: { margin: 0 } }}
                    />
                    <DefaultButton
                        iconProps={{ iconName: 'Refresh' }}
                        text="Obnovit"
                        onClick={onRefresh}
                        disabled={loading}
                    />
                </Stack>
            </Stack>

            {/* Metric tiles */}
            <Stack horizontal tokens={{ childrenGap: 12 }} wrap>
                {metrics.map((m, i) => (
                    <div key={i} style={metricTileStyle}>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }} style={{ marginBottom: 4 }}>
                            <Icon iconName={m.icon} style={{ fontSize: 11, color: fluent.palette.themePrimary }} />
                            <Text style={{ fontSize: 11, color: fluent.palette.neutralSecondary }}>{m.label}</Text>
                        </Stack>
                        <Text style={{
                            fontSize: 22,
                            fontWeight: FontWeights.bold as any,
                            color: fluent.palette.themeDark,
                            lineHeight: '1.1',
                            display: 'block',
                        }}>
                            {m.value}
                        </Text>
                    </div>
                ))}
            </Stack>
        </div>
    );
};
